/**
 * One analytics read for a screen: loads when its inputs change, keeps the previous answer on
 * screen while the next one loads (`stale`: the screen dims it, no skeleton, no jump), and says
 * when it failed. Nothing else: what to read is the caller's.
 */

import { useEffect, useState, type DependencyList } from "react";

export interface Report<T> {
  data: T | null;
  /** A newer read is in flight; `data` is the previous answer. */
  stale: boolean;
  /** The last read failed (no data, or the backend has no analytics yet). */
  failed: boolean;
}

export function useReport<T>(load: () => Promise<T>, deps: DependencyList, options: { refreshMs?: number } = {}): Report<T> {
  const [state, setState] = useState<Report<T>>({ data: null, stale: false, failed: false });

  useEffect(() => {
    let live = true;
    const read = () => {
      setState((s) => ({ ...s, stale: s.data !== null }));
      load()
        .then((data) => { if (live) setState({ data, stale: false, failed: false }); })
        .catch(() => { if (live) setState((s) => ({ data: s.data, stale: false, failed: true })); });
    };
    read();
    const timer = options.refreshMs ? window.setInterval(() => { if (document.visibilityState === "visible") read(); }, options.refreshMs) : undefined;
    return () => { live = false; if (timer) window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
